import { FC } from 'react';
import { CreditCard, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { CustomerPaymentMethod } from '../types';
import RemovePaymentMethodButton from './remove-payment-method-button';

interface Props {
  methods: CustomerPaymentMethod[];
  selectedId: string | number | null;
  contractId: string | number;
  invalidateKey: unknown[];
  onSelect: (id: string | number) => void;
  onAddNew: () => void;
  onRemoved: (removedId: string | number) => void;
  addingNew: boolean;
}

const PaymentMethodSelector: FC<Props> = ({
  methods,
  selectedId,
  contractId,
  invalidateKey,
  onSelect,
  onAddNew,
  onRemoved,
  addingNew,
}) => {
  const { t } = useTranslation(['checkout']);
  const canRemove = methods.length > 1;

  return (
    <motion.div className="space-y-3" layout>
      {methods.map((method, i) => {
        const isSelected = selectedId === method.id && !addingNew;
        return (
          <motion.div
            key={method.id}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ duration: 0.22, delay: i * 0.05 }}
            className={cn(
              'flex items-center rounded-lg border transition-colors',
              isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-input bg-background hover:bg-accent/50'
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(method.id)}
              className="flex flex-1 items-center gap-4 p-4 text-left min-w-0"
            >
              <div
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                  isSelected ? 'border-primary' : 'border-muted-foreground'
                )}
              >
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className="size-2 rounded-full bg-primary"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                    />
                  )}
                </AnimatePresence>
              </div>
              <CreditCard className="size-5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="font-medium capitalize text-sm">
                  {method.card_details?.brand} •••• {method.card_details?.last_four}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('checkout:payment-method-expires', 'Expires')}{' '}
                  {method.card_details?.exp_month}/{method.card_details?.exp_year}
                </p>
              </div>
            </button>
            {canRemove && (
              <div className="pr-3">
                <RemovePaymentMethodButton
                  methodId={method.id}
                  contractId={contractId}
                  invalidateKey={invalidateKey}
                  onRemoved={() => onRemoved(method.id)}
                />
              </div>
            )}
          </motion.div>
        );
      })}

      <motion.button
        layout
        type="button"
        onClick={onAddNew}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.22, delay: methods.length * 0.05 }}
        className={cn(
          'w-full flex items-center gap-4 rounded-lg border p-4 text-left transition-colors',
          addingNew
            ? 'border-primary bg-primary/5 ring-1 ring-primary'
            : 'border-input bg-background hover:bg-accent/50'
        )}
      >
        <div
          className={cn(
            'flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
            addingNew ? 'border-primary' : 'border-muted-foreground'
          )}
        >
          <AnimatePresence>
            {addingNew && (
              <motion.div
                className="size-2 rounded-full bg-primary"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 22 }}
              />
            )}
          </AnimatePresence>
        </div>
        <PlusCircle className="size-5 shrink-0 text-muted-foreground" />
        <span className="font-medium text-sm">
          {t('checkout:payment-method-add-new', 'Add a new card')}
        </span>
      </motion.button>
    </motion.div>
  );
};

export default PaymentMethodSelector;
